import { FormEvent, useState } from 'react';
import { Modal } from '../Modal/Modal';
import { UiContainer } from '../../state/ui';
import axios, { AxiosRequestConfig } from 'axios';
import { useLocation, useHistory, Link } from 'react-router-dom';
import './LoginModal.css';
import { useSession } from '../../state/session';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { Button } from '../Form/Button';
import { TextInput } from '../Form/TextInput';
import { useTranslation } from '../../i18n/i18n';
import { ContentCard } from '../ContentCard/ContentCard';
import { onKeyPress } from '../Form/PressedEnter';

export const LoginModal = (): JSX.Element => {
	const ui = UiContainer.useContainer();
	const history = useHistory();

	const dismiss = () => {
		ui.showLoginModal(false);
		const { pathname } = history.location;
		history.replace(pathname, null);
	};

	return (
		<Modal className="login-modal" show={ui.isLoginModalVisible} onDismiss={dismiss} toggle={dismiss}>
			<LoginCard />
		</Modal>
	);
};

export const LoginCard = (): JSX.Element => {
	const ui = UiContainer.useContainer();
	const t = useTranslation().form;
	const locationState = useLocation<{
		request: AxiosRequestConfig;
		previousLocation: string;
	}>().state;
	const { login } = useSession();
	const tRoutes = useTranslation().routes;
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loading, setLoading] = useState(false);
	const alerts = useAlerts();

	const handleLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		alerts.clear();
		setLoading(true);
		try {
			const loginResponse = await login(email, password);

			if (loginResponse?.success) {
				ui.showLoginModal(false);

				if (locationState) {
					const { request } = locationState;
					if (request) axios.request(request);
				}
				const redirectRoute =
					!!locationState && !!locationState.previousLocation ? locationState.previousLocation : '/';
				window.location.href = redirectRoute;
				return;
			}

			setLoading(false);
			alerts.set({ type: 'error', message: t.loginFailed });
		} catch {
			setLoading(false);
			alerts.set({ type: 'error', message: t.loginFailed });
		}
	};

	return (
		<ContentCard className="green login" title={t.login}>
			<form method="post" onSubmit={handleLoginSubmit}>
				<AlertMessages alerts={alerts} />
				<div className="login-grid">
					<TextInput
						type="email"
						className="area-email"
						id="login-email"
						placeholder={t.email}
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						autocomplete="email"
						autoFocus={ui.isLoginModalVisible}
						onKeyDown={onKeyPress}
						nextElement="login-password"
					/>
					<TextInput
						type="password"
						className="area-password"
						id="login-password"
						placeholder={t.password}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						autocomplete="current-password"
					/>
					<Button disabled={loading} className="area-button">
						{loading ? t.loggingIn : t.login}
					</Button>
					<div className="area-forgot">
						<Link to={tRoutes.resetPassword} onClick={() => ui.showLoginModal(false)} className="secondary">
							{t.forgotPassword}
						</Link>
						<Link to={tRoutes.createAccount} className="secondary">
							{t.createAccount}
						</Link>
					</div>
				</div>
			</form>
		</ContentCard>
	);
};
